import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, Package, Receipt, CalendarClock, BarChart3, LogOut,
  Plus, Pencil, Trash2, Download, Power, X, ShieldCheck, Images,
} from "lucide-react";
import { adminApi, money, formatError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { ProductImage } from "@/components/ProductImage";

const CATS = ["Bakery", "Fish", "Grains, Flour & Staples", "Meat", "Poultry", "Seasonings", "Vegetables", "Drinks", "Snacks", "Beauty & Hair"];
const STATUSES = ["Order Received", "Processing", "Ready", "Delivered"];
const input = "w-full bg-[#F5F8F6] rounded-[12px] px-3 py-2.5 text-[#122117] focus:outline-none focus:ring-2 focus:ring-[#0B7A3B] border-none text-sm";
const CONF = {
  high: { label: "Strong match", cls: "bg-[#DDF0E5] text-[#0B7A3B]" },
  medium: { label: "Likely match", cls: "bg-amber-100 text-amber-700" },
  low: { label: "Please check", cls: "bg-orange-100 text-orange-700" },
  none: { label: "Needs assigning", cls: "bg-red-100 text-red-600" },
  manual: { label: "Assigned by you", cls: "bg-blue-100 text-blue-700" },
};

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("pm_admin_token"));
  const [tab, setTab] = useState("dashboard");
  const navigate = useNavigate();

  if (!token) return <AdminLogin onLogin={(t) => { localStorage.setItem("pm_admin_token", t); setToken(t); }} />;

  const logout = () => { localStorage.removeItem("pm_admin_token"); setToken(null); };
  const tabs = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { id: "products", label: "Products", icon: Package },
    { id: "orders", label: "Orders", icon: Receipt },
    { id: "marketday", label: "Market Day", icon: CalendarClock },
    { id: "reports", label: "Reports", icon: BarChart3 },
  ];

  return (
    <div className="min-h-screen bg-[#F5F8F6]">
      <header className="bg-white border-b border-[#F5F8F6] sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size={38} />
            <span className="hidden sm:inline text-xs font-bold bg-[#DDF0E5] text-[#0B7A3B] rounded-full px-3 py-1">Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/market")} className="text-sm font-semibold text-[#6B8375] hover:text-[#0B7A3B]">View shop</button>
            <button onClick={logout} className="flex items-center gap-1 text-sm font-bold text-[#C0392B]" data-testid="admin-logout"><LogOut size={16} /> Logout</button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-4 flex gap-1 overflow-x-auto no-scrollbar">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-bold border-b-2 whitespace-nowrap transition-colors ${tab === t.id ? "border-[#0B7A3B] text-[#0B7A3B]" : "border-transparent text-[#6B8375]"}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {tab === "dashboard" && <Dashboard />}
        {tab === "products" && <Products />}
        {tab === "orders" && <Orders />}
        {tab === "marketday" && <MarketDay />}
        {tab === "reports" && <Reports />}
      </main>
    </div>
  );
}

function AdminLogin({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await adminApi.post("/admin/login", { username, password });
      onLogin(data.token);
      toast.success("Welcome, admin");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#095F2E] to-[#0B7A3B] px-6">
      <div className="bg-white rounded-[24px] p-6 w-full max-w-sm shadow-2xl">
        <div className="flex flex-col items-center text-center mb-5">
          <div className="w-14 h-14 rounded-2xl bg-[#DDF0E5] flex items-center justify-center mb-3"><ShieldCheck size={28} className="text-[#0B7A3B]" /></div>
          <h1 className="text-2xl font-extrabold text-[#122117]">Admin Login</h1>
          <p className="text-sm text-[#6B8375]">Pepperonni Market dashboard</p>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <input className={input} placeholder="Username" value={username} onChange={(e) => setUsername(e.target.value)} data-testid="admin-username" required />
          <input className={input} type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password" required />
          <button type="submit" disabled={loading} className="w-full bg-[#0B7A3B] text-white font-bold rounded-full py-3 active:scale-95 transition-transform disabled:opacity-60" data-testid="admin-login-submit">
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Dashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { adminApi.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);
  if (!stats) return <p className="text-[#6B8375]">Loading…</p>;
  const cards = [
    { label: "Total products", value: stats.total_products, color: "text-[#0B7A3B]" },
    { label: "Active products", value: stats.active_products, color: "text-[#0B7A3B]" },
    { label: "Total orders", value: stats.total_orders, color: "text-[#122117]" },
    { label: "Pending orders", value: stats.pending_orders, color: "text-[#C0392B]" },
  ];
  return (
    <div>
      <h1 className="text-2xl font-extrabold text-[#122117] mb-4">Dashboard</h1>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="bg-white rounded-[20px] p-5" data-testid={`stat-${c.label.replace(/\s/g, "-").toLowerCase()}`}>
            <p className="text-sm text-[#6B8375]">{c.label}</p>
            <p className={`text-3xl font-extrabold mt-1 ${c.color}`}>{c.value}</p>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-[20px] p-5 mt-4">
        <p className="text-sm text-[#6B8375]">Total revenue (all orders)</p>
        <p className="text-3xl font-extrabold text-[#0B7A3B] mt-1">{money(stats.revenue)}</p>
      </div>
    </div>
  );
}

const emptyProduct = { name: "", price: "", unit: "per kg", category: "Vegetables", available: true, image: "" };

function Products() {
  const [products, setProducts] = useState([]);
  const [modal, setModal] = useState(null); // {mode, data}
  const [bulkOpen, setBulkOpen] = useState(false);
  const load = useCallback(() => adminApi.get("/admin/products").then(({ data }) => setProducts(data)), []);
  useEffect(() => { load(); }, [load]);

  const save = async (form) => {
    const payload = { ...form, price: parseFloat(form.price) || 0 };
    try {
      if (modal.mode === "edit") await adminApi.put(`/admin/products/${modal.data.product_id}`, payload);
      else await adminApi.post("/admin/products", payload);
      toast.success("Saved");
      setModal(null);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const toggle = async (p) => { await adminApi.patch(`/admin/products/${p.product_id}/toggle`); load(); };
  const del = async (p) => { if (!window.confirm(`Delete ${p.name}?`)) return; await adminApi.delete(`/admin/products/${p.product_id}`); toast.success("Deleted"); load(); };

  const exportCsv = () => {
    const rows = [["Name", "Category", "Price", "Unit", "Available"], ...products.map((p) => [p.name, p.category, p.price, p.unit, p.available])];
    downloadCsv(rows, "products.csv");
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-extrabold text-[#122117]">Products ({products.length})</h1>
        <div className="flex gap-2">
          <button onClick={exportCsv} className="flex items-center gap-1.5 bg-white text-[#122117] font-bold rounded-full px-4 py-2 text-sm border border-[#F5F8F6]" data-testid="products-export"><Download size={15} /> CSV</button>
          <button onClick={() => setBulkOpen(true)} className="flex items-center gap-1.5 bg-[#DDF0E5] text-[#0B7A3B] font-bold rounded-full px-4 py-2 text-sm active:scale-95" data-testid="bulk-photos"><Images size={15} /> Bulk photos</button>
          <button onClick={() => setModal({ mode: "add", data: emptyProduct })} className="flex items-center gap-1.5 bg-[#0B7A3B] text-white font-bold rounded-full px-4 py-2 text-sm active:scale-95" data-testid="add-product"><Plus size={15} /> Add</button>
        </div>
      </div>

      <div className="bg-white rounded-[20px] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F8F6] text-[#6B8375]">
              <tr>
                <th className="text-left p-3 font-semibold">Product</th>
                <th className="text-left p-3 font-semibold hidden sm:table-cell">Category</th>
                <th className="text-left p-3 font-semibold">Price</th>
                <th className="text-left p-3 font-semibold">Status</th>
                <th className="text-right p-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.product_id} className="border-t border-[#F5F8F6]" data-testid={`prow-${p.product_id}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <ProductImage src={p.image} alt="" className="w-9 h-9 rounded-lg object-cover" iconSize={16} />
                      <div><p className="font-bold text-[#122117]">{p.name}</p><p className="text-xs text-[#6B8375]">{p.unit}</p></div>
                    </div>
                  </td>
                  <td className="p-3 hidden sm:table-cell text-[#6B8375]">{p.category}</td>
                  <td className="p-3 font-bold text-[#0B7A3B]">{money(p.price)}</td>
                  <td className="p-3">
                    <button onClick={() => toggle(p)} data-testid={`toggle-${p.product_id}`} className={`text-xs font-bold rounded-full px-2.5 py-1 ${p.available ? "bg-[#DDF0E5] text-[#0B7A3B]" : "bg-gray-200 text-gray-500"}`}>
                      {p.available ? "Available" : "Sold out"}
                    </button>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => setModal({ mode: "edit", data: { ...p } })} className="text-[#6B8375] hover:text-[#0B7A3B]" data-testid={`edit-${p.product_id}`}><Pencil size={16} /></button>
                      <button onClick={() => del(p)} className="text-[#C0392B]" data-testid={`del-${p.product_id}`}><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modal && <ProductModal modal={modal} onClose={() => setModal(null)} onSave={save} />}
      {bulkOpen && <BulkPhotoModal products={products} onClose={() => setBulkOpen(false)} onDone={() => { setBulkOpen(false); load(); }} />}
    </div>
  );
}

function ProductModal({ modal, onClose, onSave }) {
  const [form, setForm] = useState(modal.data);
  const [uploading, setUploading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await adminApi.post("/admin/upload", fd);
      setForm((prev) => ({ ...prev, image: data.url }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-[24px] p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="product-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-extrabold text-[#122117]">{modal.mode === "edit" ? "Edit product" : "Add product"}</h2>
          <button onClick={onClose}><X size={20} className="text-[#6B8375]" /></button>
        </div>
        <div className="space-y-3">
          <input className={input} placeholder="Name" value={form.name} onChange={set("name")} data-testid="pm-name" />
          <div className="grid grid-cols-2 gap-3">
            <input className={input} placeholder="Price" type="number" step="0.01" value={form.price} onChange={set("price")} data-testid="pm-price" />
            <input className={input} placeholder="Unit (e.g. per kg)" value={form.unit} onChange={set("unit")} data-testid="pm-unit" />
          </div>
          <select className={input} value={form.category} onChange={set("category")} data-testid="pm-category">
            {CATS.map((c) => <option key={c}>{c}</option>)}
          </select>
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              {form.image ? (
                <img src={form.image} alt="preview" className="w-14 h-14 rounded-lg object-cover border border-[#F5F8F6]" />
              ) : (
                <div className="w-14 h-14 rounded-lg bg-[#F5F8F6] flex items-center justify-center text-[#6B8375] text-xs">No photo</div>
              )}
              <label className="flex-1 cursor-pointer bg-[#DDF0E5] text-[#0B7A3B] font-bold rounded-full px-4 py-2.5 text-sm text-center active:scale-95 transition-transform" data-testid="pm-upload-label">
                {uploading ? "Uploading…" : "Upload photo"}
                <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="pm-upload" disabled={uploading} />
              </label>
            </div>
            <input className={input} placeholder="…or paste an image URL" value={form.image} onChange={set("image")} data-testid="pm-image" />
          </div>
          <label className="flex items-center gap-2 text-sm font-semibold text-[#122117]">
            <input type="checkbox" checked={form.available} onChange={(e) => setForm({ ...form, available: e.target.checked })} data-testid="pm-available" /> Available
          </label>
          <button onClick={() => onSave(form)} className="w-full bg-[#0B7A3B] text-white font-bold rounded-full py-3 active:scale-95" data-testid="pm-save">Save product</button>
        </div>
      </div>
    </div>
  );
}

function BulkPhotoModal({ products, onClose, onDone }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const norm = (s) => s.toLowerCase().replace(/\.[^.]+$/, "").replace(/[^a-z0-9]+/g, " ").replace(/\s+/g, " ").trim();

  const bestMatch = (filename) => {
    const base = norm(filename);
    if (!base) return { productId: "", confidence: "none" };
    const bWords = base.split(" ").filter(Boolean);
    let best = { productId: "", score: 0 };
    for (const p of products) {
      const name = norm(p.name.split("(")[0]);
      const nWords = name.split(" ").filter(Boolean);
      let score;
      if (name === base) {
        score = 1;
      } else {
        const inter = bWords.filter((w) => nWords.includes(w));
        const overlap = inter.length / Math.max(bWords.length, nWords.length, 1);
        const sub = name.includes(base) || base.includes(name) ? 0.6 : 0;
        score = Math.max(overlap, sub);
        if (inter.some((w) => w.length >= 4)) score += 0.15;
      }
      if (score > best.score) best = { productId: p.product_id, score };
    }
    let confidence = "none";
    if (best.score >= 0.85) confidence = "high";
    else if (best.score >= 0.5) confidence = "medium";
    else if (best.score >= 0.25) confidence = "low";
    return { productId: confidence === "none" ? "" : best.productId, confidence };
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setRows((r) => [
      ...r,
      ...files.map((f) => {
        const m = bestMatch(f.name);
        return { id: `${f.name}-${Math.random()}`, name: f.name, url: "", productId: m.productId, confidence: m.confidence, uploading: true };
      }),
    ]);
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await adminApi.post("/admin/upload", fd);
        setRows((r) => r.map((row) => (row.name === f.name && !row.url && row.uploading ? { ...row, url: data.url, uploading: false } : row)));
      } catch {
        setRows((r) => r.map((row) => (row.name === f.name && row.uploading ? { ...row, uploading: false } : row)));
        toast.error(`Upload failed: ${f.name}`);
      }
    }
  };

  const setRowProduct = (id, pid) => setRows((r) => r.map((row) => (row.id === id ? { ...row, productId: pid, confidence: pid ? "manual" : "none" } : row)));

  const saveAll = async () => {
    const ready = rows.filter((r) => r.url && r.productId);
    if (!ready.length) {
      toast.error("Upload photos and assign each to a product");
      return;
    }
    setBusy(true);
    try {
      await Promise.all(ready.map((r) => adminApi.patch(`/admin/products/${r.productId}/image`, { image: r.url })));
      toast.success(`Updated ${ready.length} product photo(s)`);
      onDone();
    } catch {
      toast.error("Some updates failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-[24px] p-5 w-full max-w-lg max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="bulk-modal">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-extrabold text-[#122117]">Bulk photo upload</h2>
          <button onClick={onClose}><X size={20} className="text-[#6B8375]" /></button>
        </div>
        <p className="text-sm text-[#6B8375] mb-3">Select multiple images, then assign each to a product. We auto-match by filename and show a confidence hint so you can spot mismatches fast.</p>
        <label className="block cursor-pointer bg-[#DDF0E5] text-[#0B7A3B] font-bold rounded-full px-4 py-2.5 text-sm text-center active:scale-95 transition-transform" data-testid="bulk-select-label">
          Choose images
          <input type="file" accept="image/*" multiple className="hidden" onChange={onFiles} data-testid="bulk-select" />
        </label>
        <div className="mt-4 space-y-2">
          {rows.map((row) => (
            <div key={row.id} className="flex items-center gap-3 border border-[#F5F8F6] rounded-[12px] p-2" data-testid="bulk-row">
              {row.uploading ? (
                <div className="w-12 h-12 rounded-lg bg-[#F5F8F6] animate-pulse shrink-0" />
              ) : row.url ? (
                <img src={row.url} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center text-[10px] text-red-500 shrink-0">fail</div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-xs text-[#6B8375] truncate">{row.name}</p>
                  {row.url && (
                    <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 shrink-0 ${CONF[row.confidence].cls}`} data-testid="bulk-confidence" title="Match confidence">
                      {CONF[row.confidence].label}
                    </span>
                  )}
                </div>
                <select className={`${input} mt-1`} value={row.productId} onChange={(e) => setRowProduct(row.id, e.target.value)} data-testid="bulk-assign">
                  <option value="">— assign to product —</option>
                  {products.map((p) => <option key={p.product_id} value={p.product_id}>{p.name}</option>)}
                </select>
              </div>
            </div>
          ))}
        </div>
        <button onClick={saveAll} disabled={busy} className="w-full mt-4 bg-[#0B7A3B] text-white font-bold rounded-full py-3 active:scale-95 disabled:opacity-60" data-testid="bulk-save">
          {busy ? "Saving…" : "Save all photos"}
        </button>
      </div>
    </div>
  );
}

function Orders() {
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState("All");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const load = useCallback(() => {
    adminApi.get("/admin/orders", { params: { status, start: start || undefined, end: end || undefined } }).then(({ data }) => setOrders(data));
  }, [status, start, end]);
  useEffect(() => { load(); }, [load]);

  const updateStatus = async (o, s) => { await adminApi.patch(`/admin/orders/${o.order_id}/status`, { status: s }); toast.success("Status updated"); load(); };
  const del = async (o) => { if (!window.confirm(`Delete order #${o.order_number}?`)) return; await adminApi.delete(`/admin/orders/${o.order_id}`); load(); };

  return (
    <div>
      <h1 className="text-2xl font-extrabold text-[#122117] mb-4">Orders ({orders.length})</h1>
      <div className="flex flex-wrap gap-2 mb-4">
        <select className={`${input} w-auto`} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="order-filter-status">
          {["All", ...STATUSES].map((s) => <option key={s}>{s}</option>)}
        </select>
        <input type="date" className={`${input} w-auto`} value={start} onChange={(e) => setStart(e.target.value)} data-testid="order-filter-start" />
        <input type="date" className={`${input} w-auto`} value={end} onChange={(e) => setEnd(e.target.value)} data-testid="order-filter-end" />
      </div>

      <div className="space-y-3">
        {orders.length === 0 && <p className="text-[#6B8375]">No orders match these filters.</p>}
        {orders.map((o) => (
          <div key={o.order_id} className="bg-white rounded-[18px] p-4" data-testid={`admin-order-${o.order_number}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-extrabold text-[#122117]">#{o.order_number} · {o.name}</p>
                <p className="text-xs text-[#6B8375]">{o.phone} · {new Date(o.created_at).toLocaleString("en-GB")}</p>
                <p className="text-xs text-[#6B8375] mt-1">{o.address}, {o.postcode} ({o.delivery_zone})</p>
              </div>
              <div className="flex items-center gap-2">
                <select value={o.status} onChange={(e) => updateStatus(o, e.target.value)} data-testid={`admin-status-${o.order_number}`} className="text-sm font-bold rounded-full px-3 py-1.5 bg-[#DDF0E5] text-[#0B7A3B] border-none focus:outline-none">
                  {STATUSES.map((s) => <option key={s}>{s}</option>)}
                </select>
                <button onClick={() => del(o)} className="text-[#C0392B]" data-testid={`admin-del-order-${o.order_number}`}><Trash2 size={16} /></button>
              </div>
            </div>
            <div className="mt-3 bg-[#F5F8F6] rounded-[12px] p-3 text-sm space-y-1">
              {o.items.map((i) => <div key={i.product_id} className="flex justify-between"><span>{i.name} × {i.quantity}</span><span className="font-semibold">{money(i.price * i.quantity)}</span></div>)}
              <div className="flex justify-between font-extrabold pt-1 border-t border-white mt-1"><span>Total</span><span className="text-[#0B7A3B]">{money(o.grand_total)}</span></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MarketDay() {
  const [days, setDays] = useState([]);
  const [form, setForm] = useState({ open_at: "", close_at: "", delivery_date: "" });
  const load = useCallback(() => adminApi.get("/admin/market-day").then(({ data }) => setDays(data)), []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.open_at || !form.close_at || !form.delivery_date) { toast.error("Fill all dates"); return; }
    try {
      await adminApi.post("/admin/market-day", {
        open_at: new Date(form.open_at).toISOString(),
        close_at: new Date(form.close_at).toISOString(),
        delivery_date: new Date(form.delivery_date).toISOString(),
      });
      toast.success("Market day created & customers notified");
      setForm({ open_at: "", close_at: "", delivery_date: "" });
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const toggle = async (d) => { await adminApi.patch(`/admin/market-day/${d.id}/toggle`); load(); };

  return (
    <div>
      <h1 className="text-2xl font-extrabold text-[#122117] mb-4">Market Day</h1>
      <div className="bg-white rounded-[20px] p-5 mb-5">
        <h2 className="font-extrabold text-[#122117] mb-3">Schedule a new market day</h2>
        <div className="grid sm:grid-cols-3 gap-3">
          <div><label className="text-xs text-[#6B8375]">Opens</label><input type="datetime-local" className={input} value={form.open_at} onChange={(e) => setForm({ ...form, open_at: e.target.value })} data-testid="md-open" /></div>
          <div><label className="text-xs text-[#6B8375]">Closes</label><input type="datetime-local" className={input} value={form.close_at} onChange={(e) => setForm({ ...form, close_at: e.target.value })} data-testid="md-close" /></div>
          <div><label className="text-xs text-[#6B8375]">Delivery date</label><input type="date" className={input} value={form.delivery_date} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} data-testid="md-delivery" /></div>
        </div>
        <button onClick={create} className="mt-4 bg-[#0B7A3B] text-white font-bold rounded-full px-6 py-2.5 active:scale-95" data-testid="md-create">Activate market day</button>
      </div>

      <div className="bg-white rounded-[20px] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F8F6] text-[#6B8375]"><tr>
            <th className="text-left p-3 font-semibold">Opens</th><th className="text-left p-3 font-semibold">Closes</th>
            <th className="text-left p-3 font-semibold hidden sm:table-cell">Delivery</th><th className="text-left p-3 font-semibold">Status</th>
          </tr></thead>
          <tbody>
            {days.map((d) => (
              <tr key={d.id} className="border-t border-[#F5F8F6]" data-testid={`md-row-${d.id}`}>
                <td className="p-3">{new Date(d.open_at).toLocaleString("en-GB")}</td>
                <td className="p-3">{new Date(d.close_at).toLocaleString("en-GB")}</td>
                <td className="p-3 hidden sm:table-cell">{new Date(d.delivery_date).toLocaleDateString("en-GB")}</td>
                <td className="p-3">
                  <button onClick={() => toggle(d)} data-testid={`md-toggle-${d.id}`} className={`flex items-center gap-1 text-xs font-bold rounded-full px-3 py-1.5 ${d.active ? "bg-[#0B7A3B] text-white" : "bg-gray-200 text-gray-600"}`}>
                    <Power size={13} /> {d.active ? "Active" : "Inactive"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Reports() {
  const [type, setType] = useState("shopping-list");
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    setData(null);
    adminApi
      .get(`/admin/reports/${type}`)
      .then(({ data }) => {
        if (alive) setData(data);
      })
      .catch(() => {
        if (alive) setData(null);
      });
    return () => {
      alive = false;
    };
  }, [type]);

  const exportCsv = async () => {
    const res = await adminApi.get(`/admin/reports/${type}`, { params: { csv_export: true }, responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url; a.download = `${type}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const types = [
    { id: "shopping-list", label: "Shopping List" },
    { id: "customer-wise", label: "Customer-Wise" },
    { id: "grouped", label: "By Category" },
  ];

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h1 className="text-2xl font-extrabold text-[#122117]">Reports</h1>
        <button onClick={exportCsv} className="flex items-center gap-1.5 bg-[#0B7A3B] text-white font-bold rounded-full px-4 py-2 text-sm active:scale-95" data-testid="report-export"><Download size={15} /> Export CSV</button>
      </div>
      <div className="flex gap-2 mb-4">
        {types.map((t) => (
          <button key={t.id} onClick={() => setType(t.id)} data-testid={`report-${t.id}`}
            className={`text-sm font-bold rounded-full px-4 py-2 ${type === t.id ? "bg-[#0B7A3B] text-white" : "bg-white text-[#6B8375]"}`}>{t.label}</button>
        ))}
      </div>

      {!data ? <p className="text-[#6B8375]">Loading…</p> : (
        <div className="bg-white rounded-[20px] p-5" data-testid="report-content">
          {type === "shopping-list" && data.items && data.summary && (
            <div>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <Kpi label="Orders" value={data.summary.total_orders} />
                <Kpi label="Items" value={data.summary.total_items} />
                <Kpi label="Value" value={money(data.summary.total_value)} />
              </div>
              <ReportTable head={["Product", "Unit", "Qty", "Value"]} rows={data.items.map((i) => [i.name, i.unit, i.quantity, money(i.total)])} />
            </div>
          )}
          {type === "customer-wise" && data.orders && (
            <div className="space-y-3">
              {data.orders.map((o) => (
                <div key={o.order_id} className="border border-[#F5F8F6] rounded-[14px] p-3">
                  <p className="font-bold text-[#122117]">#{o.order_number} · {o.name} · {o.phone}</p>
                  <p className="text-xs text-[#6B8375] mb-2">{o.address}, {o.postcode} · {o.status}</p>
                  <div className="text-sm space-y-0.5">
                    {o.items.map((i) => <div key={i.product_id} className="flex justify-between"><span>{i.name} × {i.quantity}</span><span>{money(i.price * i.quantity)}</span></div>)}
                  </div>
                  <p className="text-right font-extrabold text-[#0B7A3B] mt-1">{money(o.grand_total)}</p>
                </div>
              ))}
              {data.orders.length === 0 && <p className="text-[#6B8375]">No orders.</p>}
            </div>
          )}
          {type === "grouped" && data.groups && (
            <div className="space-y-4">
              {data.groups.map((g) => (
                <div key={g.category}>
                  <div className="flex justify-between mb-1"><h3 className="font-extrabold text-[#122117]">{g.category}</h3><span className="font-bold text-[#0B7A3B]">{money(g.total)}</span></div>
                  <ReportTable head={["Product", "Unit", "Qty", "Value"]} rows={g.items.map((i) => [i.name, i.unit, i.quantity, money(i.total)])} />
                </div>
              ))}
              {data.groups.length === 0 && <p className="text-[#6B8375]">No data.</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value }) {
  return <div className="bg-[#F5F8F6] rounded-[14px] p-3 text-center"><p className="text-xs text-[#6B8375]">{label}</p><p className="text-xl font-extrabold text-[#0B7A3B]">{value}</p></div>;
}

function ReportTable({ head, rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-[#6B8375]"><tr>{head.map((h, i) => <th key={i} className={`p-2 font-semibold ${i === 0 ? "text-left" : "text-right"}`}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, ri) => <tr key={ri} className="border-t border-[#F5F8F6]">{r.map((c, ci) => <td key={ci} className={`p-2 ${ci === 0 ? "text-left font-semibold text-[#122117]" : "text-right text-[#6B8375]"}`}>{c}</td>)}</tr>)}
        </tbody>
      </table>
    </div>
  );
}

function downloadCsv(rows, filename) {
  const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}
